/**
 * An item's star rating (Dax, 2026-10-01): five stars, gold up to the rounded mean of every vote once
 * anyone has rated it; unrated, all five pale grey with the first three a shade darker, the three stars
 * an unrated item counts as. Clicking a star casts this account's vote. The three-dot menu carries the
 * same choice as a submenu, best first, with a way to withdraw the vote.
 */
import { RATING_MENU_STEPS, UNRATED_STARS, type Rating } from '@studio/contracts';
import { Star } from './icons';
import type { RowMenuItem } from './row-menu';

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? '' : 's'}`;
}

function describe(r: Rating): string {
  if (!r.votes || r.mean === null) return 'Unrated';
  return `${r.mean.toFixed(1)} from ${plural(r.votes, 'rating')}${r.mine === null ? '' : ` · yours ${plural(r.mine, 'star')}`}`;
}

/** Stars in DOM order five to one, laid out one to five, so hovering a star lights it and those before it in CSS. */
export function StarRating({ rating, onRate }: { rating: Rating; onRate?: (stars: number) => void }) {
  const lit = rating.stars ?? UNRATED_STARS;
  return (
    <div className={`stars${rating.stars === null ? ' unrated' : ''}`} role="group" aria-label={`Rating: ${describe(rating)}`} title={describe(rating)}
      onClick={(e) => e.stopPropagation()}>
      {RATING_MENU_STEPS.map((n) => (
        <button key={n} type="button" className={`star${n <= lit ? ' lit' : ''}`} disabled={!onRate}
          aria-label={`Rate ${plural(n, 'star')}`} aria-pressed={rating.mine === n} onClick={() => onRate?.(n)}>
          <Star />
        </button>
      ))}
    </div>
  );
}

/** A row of n gold stars out of five, for the menu's choices. */
function StarsShown({ n }: { n: number }) {
  return (
    <span className="stars menu-stars" aria-hidden="true">
      {RATING_MENU_STEPS.map((k) => <span key={k} className={`star${k <= n ? ' lit' : ''}`}><Star /></span>)}
    </span>
  );
}

/** The three-dot menu's rating entry: it opens onto five choices, then a withdrawal once this account has voted. */
export function ratingMenuItem(rating: Rating, onRate: (stars: number | null) => void): RowMenuItem {
  return {
    label: 'Star rating',
    items: [
      ...RATING_MENU_STEPS.map((n) => ({ label: plural(n, 'star'), content: <StarsShown n={n} />, checked: rating.mine === n, onClick: () => onRate(n) })),
      ...(rating.mine === null ? [] : [{ label: 'Clear my rating', onClick: () => onRate(null) }]),
    ],
  };
}
